/**
 * My Reservations Page
 * View bookings - logged in users see all, guests lookup by booking number + email
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Search, Calendar, MapPin, Car, User,
  AlertCircle, Loader2, FileText,
  ChevronRight, Home
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import ClientKpiByType from '../components/client/ClientKpiByType';
import config from 'config';

const API_URL = config.API_URL;

// Status colors and labels
const STATUS_CONFIG = {
  draft: { label: 'Brouillon', color: 'bg-gray-500', textColor: 'text-gray-400' },
  pending_payment: { label: 'En attente de paiement', color: 'bg-yellow-500', textColor: 'text-yellow-400' },
  confirmed: { label: 'Confirmée', color: 'bg-green-500', textColor: 'text-green-400' },
  assigned: { label: 'Chauffeur assigné', color: 'bg-blue-500', textColor: 'text-blue-400' },
  in_progress: { label: 'En cours', color: 'bg-purple-500', textColor: 'text-purple-400' },
  completed: { label: 'Terminée', color: 'bg-green-600', textColor: 'text-green-500' },
  cancelled: { label: 'Annulée', color: 'bg-red-500', textColor: 'text-red-400' },
};

const BookingCard = ({ booking }) => {
  const status = STATUS_CONFIG[booking.status] || STATUS_CONFIG.draft;
  const pickupDate = new Date(booking.pickup_datetime);
  // Use booking_number if available, otherwise fallback to last 8 chars of id
  const reference = booking.booking_number || booking.id?.slice(-8).toUpperCase();
  // Use vehicle_type (updated by admin) with fallback to vehicle_category
  const vehicleType = booking.vehicle_type || booking.vehicle_category || 'berline';
  
  return (
    <Card className="bg-[#1F1F1F] border-[#C9A24D]/20 hover:border-[#C9A24D]/40 transition-all">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-[#C9A24D] font-mono text-sm">#{reference}</p>
            <p className="text-[#E5E5E5] font-medium mt-1">{booking.client_name}</p>
          </div>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.color} text-white`}>
            {status.label}
          </span>
        </div>
        
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-[#E5E5E5]/70">
            <Calendar className="w-4 h-4 text-[#C9A24D]" />
            <span>{format(pickupDate, "EEEE d MMMM yyyy 'à' HH:mm", { locale: fr })}</span>
          </div>
          
          <div className="flex items-start gap-2 text-[#E5E5E5]/70">
            <MapPin className="w-4 h-4 text-green-500 mt-0.5" />
            <span className="line-clamp-1">{booking.pickup?.address || 'N/A'}</span>
          </div>
          
          <div className="flex items-start gap-2 text-[#E5E5E5]/70">
            <MapPin className="w-4 h-4 text-red-500 mt-0.5" />
            <span className="line-clamp-1">{booking.dropoff?.address || 'N/A'}</span>
          </div>
          
          <div className="flex items-center justify-between pt-2 border-t border-[#C9A24D]/10">
            <div className="flex items-center gap-2">
              <Car className="w-4 h-4 text-[#C9A24D]" />
              <span className="text-[#E5E5E5]/70 capitalize">{vehicleType}</span>
            </div>
            <span className="text-[#C9A24D] font-bold">{booking.price?.total_ttc?.toFixed(2) || '—'} €</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const MyReservationsPage = () => {
  const { isAuthenticated } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch bookings for logged-in users
  useEffect(() => {
    if (isAuthenticated) {
      fetchMyBookings();
    }
  }, [isAuthenticated]);

  const fetchMyBookings = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_URL}/api/bookings/my/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBookings(response.data.bookings || []);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError('Erreur lors de la récupération de vos réservations');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0B0B]">
      {/* Header */}
      <header className="bg-[#1F1F1F] border-b border-[#C9A24D]/20">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link 
              to="/" 
              className="flex items-center gap-2 text-[#E5E5E5]/60 hover:text-[#C9A24D] transition-colors"
            >
              <Home className="w-4 h-4" />
              <span className="text-sm">Accueil</span>
            </Link>
            <Link to="/" className="font-['Playfair_Display'] text-2xl font-bold text-[#C9A24D]">
              Y GO
            </Link>
            <div className="w-24" />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-['Playfair_Display'] text-[#E5E5E5] mb-2">
            Mes Réservations
          </h1>
          <p className="text-[#E5E5E5]/60">
            {isAuthenticated
              ? 'Retrouvez l\'historique de toutes vos courses'
              : 'Accédez à votre réservation ou créez votre compte'
            }
          </p>
        </div>

        {/* Guest Mode — redirect to dedicated flows (Option B) */}
        {!isAuthenticated && (
          <Card className="bg-[#1F1F1F] border-[#C9A24D]/20 mb-6" data-testid="guest-ctas-card">
            <CardContent className="p-6 md:p-8">
              <div className="text-center mb-6">
                <Search className="w-10 h-10 text-[#C9A24D] mx-auto mb-3" />
                <h2 className="text-lg text-[#E5E5E5] font-['Playfair_Display'] mb-1">
                  Comment voulez-vous accéder à votre réservation&nbsp;?
                </h2>
                <p className="text-sm text-[#E5E5E5]/60">
                  Vous n'avez pas encore de compte&nbsp;? Deux options s'offrent à vous&nbsp;:
                </p>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <Link to="/suivi" className="block group" data-testid="cta-tracking">
                  <div className="h-full p-5 rounded-lg border border-[#C9A24D]/30 bg-gradient-to-br from-[#0B0B0B] to-[#1F1F1F] hover:border-[#C9A24D] hover:shadow-[0_0_20px_rgba(201,162,77,0.15)] transition-all">
                    <div className="flex items-center gap-2 mb-2">
                      <Search className="w-5 h-5 text-[#C9A24D]" />
                      <h3 className="text-[#E5E5E5] font-medium">Suivre ma course</h3>
                    </div>
                    <p className="text-xs text-[#E5E5E5]/60 leading-relaxed">
                      Entrez votre numéro de réservation pour voir le statut, suivre le chauffeur en temps réel et accéder à tous les détails.
                    </p>
                    <div className="mt-3 flex items-center gap-1 text-xs text-[#C9A24D] group-hover:gap-2 transition-all">
                      Accéder au suivi <ChevronRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </Link>
                <Link to="/register" className="block group" data-testid="cta-register">
                  <div className="h-full p-5 rounded-lg border border-[#C9A24D]/20 bg-gradient-to-br from-[#1F1F1F] to-[#161616] hover:border-[#C9A24D]/60 transition-all">
                    <div className="flex items-center gap-2 mb-2">
                      <User className="w-5 h-5 text-[#C9A24D]/80" />
                      <h3 className="text-[#E5E5E5] font-medium">Créer un compte</h3>
                    </div>
                    <p className="text-xs text-[#E5E5E5]/60 leading-relaxed">
                      Retrouvez automatiquement tout votre historique, vos futures réservations et votre tableau de bord personnalisé.
                    </p>
                    <div className="mt-3 flex items-center gap-1 text-xs text-[#C9A24D] group-hover:gap-2 transition-all">
                      Créer mon compte <ChevronRight className="w-3.5 h-3.5" />
                    </div>
                  </div>
                </Link>
              </div>
              <div className="mt-5 text-center">
                <p className="text-xs text-[#E5E5E5]/50">
                  Déjà un compte&nbsp;?{' '}
                  <Link to="/login" className="text-[#C9A24D] hover:underline">
                    Connectez-vous
                  </Link>
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-[#C9A24D] animate-spin" />
          </div>
        )}

        {/* Bookings List (Logged In Users) */}
        {isAuthenticated && !loading && (
          <div className="space-y-4">
            {/* KPI dashboard for connected clients */}
            <ClientKpiByType />

            {bookings.length === 0 ? (
              <Card className="bg-[#1F1F1F] border-[#C9A24D]/20">
                <CardContent className="p-8 text-center">
                  <FileText className="w-12 h-12 text-[#E5E5E5]/30 mx-auto mb-4" />
                  <h3 className="text-[#E5E5E5] font-medium mb-2">Aucune réservation</h3>
                  <p className="text-[#E5E5E5]/60 text-sm mb-4">
                    Vous n'avez pas encore effectué de réservation
                  </p>
                  <Link to="/booking">
                    <Button className="bg-[#C9A24D] hover:bg-[#E6C77A] text-[#0B0B0B]">
                      Réserver maintenant
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ) : (
              <>
                <p className="text-[#E5E5E5]/60 text-sm">
                  {bookings.length} réservation{bookings.length > 1 ? 's' : ''} trouvée{bookings.length > 1 ? 's' : ''}
                </p>
                <div className="grid gap-4">
                  {bookings.map((booking) => (
                    <BookingCard key={booking.id} booking={booking} />
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* CTA for booking */}
        {!loading && (
          <div className="mt-8 text-center">
            <Link to="/booking">
              <Button variant="outline" className="border-[#C9A24D] text-[#C9A24D] hover:bg-[#C9A24D]/10">
                Nouvelle réservation
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        )}

        {/* Footer */}
        <div className="mt-12 text-center">
          <p className="text-[#E5E5E5]/40 text-xs">
            © 2024 Y GO Shuttle Services France
          </p>
        </div>
      </main>
    </div>
  );
};

export default MyReservationsPage;
